import { addScript, isObject, pick } from '@tunein/web-utils';
import { GoogleIMAProvider, } from '../../ads';
import { BaseEngine, HLSEngine, HLSLiveSeekEngine } from '../../engine';
import { ErrorName, PlayerError } from '../../errors';
import { TypedEmitter } from '../../events';
import { debug, disableDebugMode, enableDebugMode } from '../../utils/logger';
import { clamp } from '../../utils/math';
import { detectMimeType } from '../../utils/mimeType';
import parsePlaylist from '../../utils/parsePlaylist';
import { applyShoutcastUrlFallback, isShoutcastUrl, shouldApplyShoutcastUrlFallback, } from '../../utils/shoutcast';
export const ADSWIZZ_SYNCHROBOX_SCRIPT = 'https://synchrobox.adswizz.com/register2.php';
export const ADSWIZZ_SYNCHROCLIENT_SCRIPT = 'https://delivery-cdn-cf.adswizz.com/adswizz/js/SynchroClient2.js';
export const DEFAULT_OPTIONS = { onlyUseCoreEngine: false };
const adProviderMap = {
    googima: GoogleIMAProvider,
};
var PlayerState;
(function (PlayerState) {
    PlayerState["PAUSED"] = "paused";
    PlayerState["PLAYING"] = "playing";
    PlayerState["IDLE"] = "idle";
    PlayerState["LOADING"] = "loading";
    PlayerState["LOADED"] = "loaded";
    PlayerState["FAILED"] = "failed";
})(PlayerState || (PlayerState = {}));
export default class WebMediaPlayer extends TypedEmitter {
    #options = { ...DEFAULT_OPTIONS };
    #engine;
    #engineEvents;
    #adProviderEvents;
    #ads;
    #volume = 0.5;
    #state = PlayerState.IDLE;
    #ssaiManager;
    #currentSourceIndex = 0;
    #src;
    #isAdBreak = false;
    constructor(options) {
        super();
        if (options) {
            this.setOptions(options);
        }
        debug('Creating WebMediaPlayer instance...');
        addScript({ src: ADSWIZZ_SYNCHROBOX_SCRIPT });
        addScript({ src: ADSWIZZ_SYNCHROCLIENT_SCRIPT });
        this.#engineEvents = new Map([
            ['abort', this.#onAbort],
            ['canplay', this.#onCanPlay],
            ['canplaythrough', this.#onCanPlayThrough],
            ['durationchange', this.#onDurationChange],
            ['emptied', this.#onEmptied],
            ['ended', this.#onEnded],
            ['error', this.#onError],
            ['loadedmetadata', this.#onLoadedMetadata],
            ['loadeddata', this.#onLoadedData],
            ['loadstart', this.#onLoadStart],
            ['meta', this.#onMeta],
            ['pause', this.#onPause],
            ['play', this.#onPlay],
            ['playing', this.#onPlaying],
            ['ratechange', this.#onRateChange],
            ['seeked', this.#onSeeked],
            ['seeking', this.#onSeeking],
            ['stalled', this.#onStalled],
            ['suspend', this.#onSuspend],
            ['texttrackcuechange', this.#onTextTrackCueChange],
            ['timeupdate', this.#onTimeUpdate],
            ['volumechange', this.#onVolumeChange],
            ['unlock', this.#onUnlock],
            ['waiting', this.#onWaiting],
        ]);
        this.#adProviderEvents = new Map([
            ['adbuffering', this.#onAdEvent],
            ['adclick', this.#onAdEvent],
            ['adcomplete', this.#onAdEvent],
            ['addurationchange', this.#onAdEvent],
            ['aderror', this.#onAdError],
            ['adimpression', this.#onAdEvent],
            ['adinteraction', this.#onAdEvent],
            ['adloaded', this.#onAdEvent],
            ['admetadata', this.#onAdEvent],
            ['adpause', this.#onAdPause],
            ['adprogress', this.#onAdEvent],
            ['adresume', this.#onAdResume],
            ['adstart', this.#onAdStart],
            ['adskipped', this.#onAdSkipped],
            ['advolumechange', this.#onAdEvent],
            ['advolumemuted', this.#onAdEvent],
            ['contentresumerequested', this.#onContentResumeRequested],
            ['contentpauserequested', this.#onContentPauseRequested],
            ['alladscomplete', this.#onAllAdsComplete],
        ]);
        this.on('error', error => {
            debug('error', error);
        });
    }
    get currentSource() {
        return this.#src?.[this.#currentSourceIndex];
    }
    getEngineName() {
        return this.#engine?.name;
    }
    setOptions(options = {}) {
        if (options.debug) {
            enableDebugMode(options.debug);
        }
        else {
            disableDebugMode();
        }
        this.#currentSourceIndex = 0;
        this.#options = { ...DEFAULT_OPTIONS, ...this.#options, ...options };
        if (options.src) {
            this.#setSources(options.src);
        }
    }
    getOptions() {
        return this.#options;
    }
    async requestAdBreak(adRequestId, adTagParams) {
        this.#isAdBreak = true;
        this.#state = PlayerState.LOADING;
        debug('play ad: setting current status to loading');
        this.#deregisterEngineEvents();
        this.#setupAdsProvider();
        await this.#ads?.load(adRequestId, adTagParams);
        this.#ads?.setVolume(this.#volume);
        this.#ads?.play();
        return new Promise(resolve => this.once('contentresumerequested', resolve));
    }
    async load(src) {
        debug('load', {
            currentState: this.#state,
            isAdBreak: this.#isAdBreak,
        });
        try {
            if (src) {
                this.#currentSourceIndex = 0;
                this.#options.src = src;
                this.#setSources(src);
            }
            if (!this.isAdBreak()) {
                debug('setting current status to idle');
                this.#state = PlayerState.IDLE;
            }
            await this.#setupEngine();
            try {
                await this.#engine?.load();
                this.#state = PlayerState.LOADED;
            }
            catch (error) {
                if (this.isFailed()) {
                    return;
                }
                debug('Trying next source...');
                this.#currentSourceIndex++;
                await this.load();
            }
            if (this.#options.autoPlay) {
                debug('Attempting to autoplay...');
                await this.play();
            }
        }
        catch (error) {
            const playerError = PlayerError.createError(ErrorName.LoadError, error?.message);
            this.#onError(playerError);
        }
    }
    stop() {
        debug('stop', {
            state: this.#state,
            isAdBreak: this.isAdBreak(),
        });
        if (!this.isIdle()) {
            this.#currentSourceIndex = 0;
            this.#state = PlayerState.IDLE;
            if (this.isAdBreak()) {
                this.#ads?.stop();
            }
            else {
                this.#engine?.stop();
            }
        }
    }
    unload() {
        debug('unload', {
            currentState: this.#state,
            isAdBreak: this.#isAdBreak,
        });
        this.#state = PlayerState.IDLE;
        this.#currentSourceIndex = 0;
        this.#unloadAdsProvider();
        this.#unloadEngine();
    }
    async play() {
        debug('play', {
            currentState: this.#state,
            isAdBreak: this.#isAdBreak,
        });
        if (this.isAdBreak() && this.isPaused()) {
            this.#state = PlayerState.PLAYING;
            this.#ads?.play();
            return;
        }
        if (this.isFailed() || this.isPlaying() || this.isLoading()) {
            return;
        }
        this.#engine?.setVolume(this.#volume);
        debug('play content: setting current status to loading');
        this.#state = PlayerState.LOADING;
        try {
            await this.#engine?.play();
        }
        catch (error) {
            const playerError = PlayerError.createError(ErrorName.InternalPlayerError, error?.message);
            this.#onError(playerError);
        }
    }
    pause() {
        debug('pause', {
            currentState: this.#state,
            isAdBreak: this.#isAdBreak,
        });
        if (this.isPlaying()) {
            this.#state = PlayerState.PAUSED;
            if (this.isAdBreak()) {
                this.#ads?.pause?.();
            }
            else {
                this.#engine?.pause();
            }
        }
    }
    mute(value) {
        this.#ads?.setVolume(value ? 0 : this.#volume);
        this.#engine?.mute(value);
    }
    seekTo(seekTime) {
        if (!this.isAdBreak()) {
            this.#engine?.seekTo(seekTime);
        }
    }
    seekBackward(seekOffset) {
        this.#engine?.seekBackward(seekOffset);
    }
    seekForward(seekOffset) {
        this.#engine?.seekForward(seekOffset);
    }
    setSSAIManager(ssaiManager) {
        if (this.#ssaiManager) {
            this.#ssaiManager.off('error', this.#onSsaiError);
            this.#ssaiManager.off('info', this.#onSsaiInfo);
        }
        this.#ssaiManager = ssaiManager;
        this.#ssaiManager.on('error', this.#onSsaiError);
        this.#ssaiManager.on('info', this.#onSsaiInfo);
    }
    setVolume(value) {
        this.#volume = clamp(value, 0, 1);
        if (this.#isAdBreak) {
            this.#ads?.setVolume?.(this.#volume);
        }
        else {
            this.#engine?.setVolume(this.#volume);
        }
    }
    getPlaybackRate() {
        return this.#engine?.getPlaybackRate();
    }
    setPlaybackRate(rate) {
        this.#engine?.setPlaybackRate(rate);
    }
    setInstreamAdParams(adParams) {
        this.#ssaiManager?.setRequestParams(adParams);
    }
    isLoading() {
        return this.#state === PlayerState.LOADING;
    }
    isLoaded() {
        return this.#state === PlayerState.LOADED;
    }
    isPlaying() {
        return this.#state === PlayerState.PLAYING;
    }
    isPaused() {
        return this.#state === PlayerState.PAUSED;
    }
    isIdle() {
        return this.#state === PlayerState.IDLE;
    }
    isFailed() {
        return this.#state === PlayerState.FAILED;
    }
    isAdBreak() {
        return this.#isAdBreak;
    }
    isLive() {
        return this.#engine?.isLive();
    }
    getDuration() {
        return this.#engine?.getDuration() || 0;
    }
    getCurrentTime() {
        return this.#engine?.getCurrentTime() || 0;
    }
    getVolume() {
        return this.#engine?.getVolume();
    }
    getTextTracks() {
        return this.#engine?.getTextTracks();
    }
    getMediaElement() {
        return this.#engine?.getElement();
    }
    getAdMediaElement() {
        return this.#ads?.getElement();
    }
    async #setSources(src) {
        let sources = [];
        const { shoutcast } = this.#options;
        if (typeof src === 'string') {
            sources = [{ url: src }];
        }
        else if (Array.isArray(src)) {
            sources = src.map(source => typeof source === 'string' ? { url: source } : source);
        }
        else if (isObject(src)) {
            sources = [src];
        }
        else {
            sources = [];
        }
        if (!shoutcast?.disable) {
            sources?.forEach(source => {
                if (shouldApplyShoutcastUrlFallback(source.url, shoutcast?.ignoredPatterns)) {
                    sources?.splice?.(sources.indexOf(source) + 1, 0, {
                        ...source,
                        url: applyShoutcastUrlFallback(source.url),
                    });
                }
            });
        }
        this.#src = sources;
    }
    #setupAdsProvider() {
        const id = this.#options.advertising?.provider;
        if (!adProviderMap[id]) {
            throw PlayerError.createError(ErrorName.UnknownAdProvider);
        }
        debug(`Setting ads provider to ${id}...`);
        this.#unloadAdsProvider();
        const adProps = {
            ...this.#options.advertising,
            autoPlay: this.#options.autoPlay,
        };
        this.#ads = new adProviderMap[id](adProps);
        this.#registerAdProviderEvents();
        debug(`Ads provider set to ${id}`);
    }
    #detectMimeType = async (url, format) => {
        debug('Detecting mime type...');
        const mimeType = await detectMimeType(url, format);
        debug(`Detected mime type: ${mimeType}`);
        return mimeType;
    };
    async #maybeParsePlaylist(src) {
        const { playlistHandler: customPlaylistHandler } = src;
        const { pathname } = new URL(src.url);
        const canParsePlaylist = src.format === 'pls' ||
            pathname.endsWith('.pls') ||
            customPlaylistHandler;
        if (!canParsePlaylist) {
            return src.url;
        }
        try {
            let playlist;
            if (customPlaylistHandler) {
                const customPlaylist = await customPlaylistHandler(src);
                playlist = Array.isArray(customPlaylist)
                    ? customPlaylist
                    : [customPlaylist];
            }
            else {
                const response = await fetch(src.url).catch(() => { });
                if (response?.ok) {
                    const rawPlaylist = await response.text();
                    playlist = parsePlaylist(rawPlaylist)?.map(url => ({ url }));
                }
            }
            if (!playlist?.length) {
                throw new Error('Empty playlist');
            }
            this.#src?.splice(this.#currentSourceIndex, 1, ...playlist);
            return this.#src?.[this.#currentSourceIndex].url;
        }
        catch (error) {
            throw PlayerError.createError(ErrorName.PlaylistParseError, error?.message);
        }
    }
    async #maybeResolveSSAI(url, isSSAI) {
        if (isSSAI) {
            await this.#ssaiManager?.requestStream(url);
            return this.#ssaiManager?.getStreamUrl() ?? url;
        }
        return url;
    }
    #selectEngine(mimeType, isLiveSeekStream, engineOptions) {
        if (this.#options.onlyUseCoreEngine) {
            debug('Set engine to HTML5');
            return new BaseEngine(engineOptions);
        }
        let engine;
        if (HLSEngine.canPlayType(mimeType)) {
            engine = isLiveSeekStream
                ? new HLSLiveSeekEngine(engineOptions)
                : new HLSEngine(engineOptions);
            debug('Set engine to HLS');
        }
        else {
            engine = new BaseEngine(engineOptions);
            debug('Set engine to HTML5');
        }
        return engine;
    }
    async #setupEngine() {
        if (!this.#src) {
            throw PlayerError.createError(ErrorName.MissingContentSource);
        }
        this.#unloadEngine();
        if (!this.#src[this.#currentSourceIndex]) {
            throw new Error('Invalid source index');
        }
        const src = this.#src[this.#currentSourceIndex];
        const { format, isSSAI, isLiveSeekStream } = src;
        let { url } = src;
        if (!url) {
            throw new Error('Missing url');
        }
        debug('Using media source: ', url, ', format: ', format);
        url = await this.#maybeParsePlaylist(src);
        url = await this.#maybeResolveSSAI(url, isSSAI);
        const mimeType = await this.#detectMimeType(url, format);
        const engineOptions = {
            src: url,
            ...pick(this.#options, [
                'autoPlay',
                'debug',
                'preload',
                'mediaElement',
                'keepMediaElementAlive',
            ]),
        };
        this.#engine = this.#selectEngine(mimeType, isLiveSeekStream, engineOptions);
        if (!this.isAdBreak()) {
            this.#registerEngineEvents();
        }
        this.#engine?.setup();
        this.#updateSsaiBeacons();
    }
    #unloadEngine = () => {
        this.#deregisterEngineEvents();
        this.#engine?.unload();
        this.#engine = undefined;
    };
    #unloadAdsProvider() {
        this.#deregisterAdProviderEvents();
        this.#ads?.unload();
    }
    #registerAdProviderEvents = () => {
        this.#adProviderEvents.forEach((cb, name) => {
            this.#ads?.on(name, cb);
        });
    };
    #deregisterAdProviderEvents = () => {
        this.#adProviderEvents.forEach((cb, name) => {
            this.#ads?.off(name, cb);
        });
    };
    #registerEngineEvents = () => {
        this.#engineEvents.forEach((cb, name) => {
            this.#engine?.on(name, cb);
        });
    };
    #deregisterEngineEvents = () => {
        this.#engineEvents.forEach((cb, name) => {
            this.#engine?.off(name, cb);
        });
    };
    #updateSsaiBeacons = () => {
        const el = this.#engine?.getElement();
        if (el) {
            this.#ssaiManager?.updateBeaconListeners(el);
        }
    };
    #onSsaiError = (error) => {
        this.#onError(error);
    };
    #onSsaiInfo = (info) => {
        this.emit('info', info);
    };
    #onAbort = () => {
        this.emit('abort');
    };
    #onCanPlay = () => {
        this.#emitWithSource('canplay');
    };
    #onCanPlayThrough = () => {
        this.emit('canplaythrough');
    };
    #onDurationChange = () => {
        this.emit('durationchange');
    };
    #onEmptied = () => {
        this.emit('emptied');
    };
    #onEnded = () => {
        this.#state = PlayerState.IDLE;
        this.emit('ended');
    };
    #onLoadedMetadata = () => {
        this.emit('loadedmetadata');
    };
    #onLoadedData = () => {
        this.emit('loadeddata');
    };
    #onLoadStart = () => {
        this.emit('loadstart');
    };
    #onMeta = (event) => {
        this.emit('meta', event);
        this.#ssaiManager?.processMetadata(event);
    };
    #onPause = () => {
        this.#state = PlayerState.PAUSED;
        this.emit('pause');
    };
    #onPlay = () => {
        this.emit('play');
    };
    #onPlaying = () => {
        debug('setting state to playing');
        this.#state = PlayerState.PLAYING;
        this.emit('playing');
    };
    #emitWithSource = (eventType, error) => {
        const eventData = {
            index: this.#currentSourceIndex,
            url: this.#src?.[this.#currentSourceIndex].url,
            isShoutcastUrl: isShoutcastUrl(this.#src?.[this.#currentSourceIndex].url || ''),
            error,
        };
        if (this.#src?.length &&
            this.#currentSourceIndex === this.#src?.length - 1) {
            eventData.isLastSource = true;
        }
        this.emit(eventType, eventData);
    };
    #onError = async (error, context = 'player') => {
        this.emit('error', {
            error: PlayerError.createError(error, error?.message),
            context,
        });
        if (!error?.fatal) {
            return;
        }
        this.#emitWithSource('sourcefailed', error);
        if (error.name === 'NotAllowedError') {
            this.#state = PlayerState.FAILED;
            return;
        }
        if (!this.#src?.[this.#currentSourceIndex + 1]) {
            this.#state = PlayerState.FAILED;
            this.emit('allsourcesfailed');
            return;
        }
    };
    #onRateChange = () => {
        this.emit('ratechange');
    };
    #onSeeked = async () => {
        this.emit('seeked');
    };
    #onSeeking = () => {
        this.emit('seeking');
    };
    #onStalled = () => {
        this.emit('stalled');
    };
    #onSuspend = () => {
        this.emit('suspend');
    };
    #onTextTrackCueChange = (event) => {
        this.emit('texttrackcuechange', event);
    };
    #onTimeUpdate = () => {
        this.emit('timeupdate');
    };
    #onVolumeChange = () => {
        this.emit('volumechange');
    };
    #onWaiting = () => {
        this.emit('waiting');
    };
    #onUnlock = async () => {
        debug('Autoplay functionality unlocked');
        if (this.#options.autoPlay) {
            debug('Attempting to autoplay...');
            await this.play();
        }
        this.emit('unlock');
    };
    #onContentResumeRequested = async () => {
        this.#state = PlayerState.IDLE;
        this.#isAdBreak = false;
        this.emit('contentresumerequested');
    };
    #onContentPauseRequested = async () => {
        if (this.isPlaying()) {
            this.#state = PlayerState.PAUSED;
            this.#engine?.pause();
        }
        this.emit('contentpauserequested');
    };
    #onAdEvent = (event) => {
        this.emit(event?.eventType, event);
    };
    #onAllAdsComplete = () => {
        this.#state = PlayerState.IDLE;
        this.#isAdBreak = false;
        this.emit('alladscomplete');
    };
    #onAdSkipped = (event) => {
        if (event.adPosition === event.totalAds) {
            this.#state = PlayerState.IDLE;
            this.#isAdBreak = false;
        }
        this.emit('adskipped');
    };
    #onAdError = async (errorData) => {
        debug('Ad error', errorData);
        this.#state = PlayerState.IDLE;
        this.#isAdBreak = false;
        this.emit('aderror', errorData);
        if (!this.#ads?.isInitializing) {
            await this.play();
        }
    };
    #onAdPause = (event) => {
        this.#state = PlayerState.PAUSED;
        this.emit('adpause', event);
    };
    #onAdResume = (event) => {
        this.#state = PlayerState.PLAYING;
        this.emit('adresume', event);
    };
    #onAdStart = (event) => {
        this.#state = PlayerState.PLAYING;
        this.#isAdBreak = true;
        this.emit('adstart', event);
    };
}
